import type { Quote, QuoteItem } from './types';

export function quoteTableGroupKey(item: QuoteItem): string {
  if (item.containerInstanceId) return `container:${item.containerInstanceId}`;
  if (item.sourceFragment)
    return `fragment:${item.sourceFragment.instanceKey}:package:${item.packageId ?? ''}:membership:${item.planPackageMembershipId ?? ''}:occurrence:${item.formalPlanOccurrence?.occurrenceKey ?? ''}`;
  if (item.quoteGroupId) return `group:${item.quoteGroupId}`;
  const occurrenceKey = item.formalPlanOccurrence?.occurrenceKey;
  const packageRevisionId =
    item.formalPlanOccurrence?.packageRevisionId ?? item.packageRevisionId;
  return `${item.packageId ?? `device:${item.deviceId}`}::${item.sourceType}:${item.planId ?? item.sourceId}::${item.sourceRevisionId ?? ''}::${packageRevisionId ?? ''}::${item.planPackageMembershipId ?? ''}::${item.packageOriginPlanId ?? ''}:${item.packageOriginPlanRevisionId ?? ''}${occurrenceKey ? `::occurrence:${occurrenceKey}` : ''}${item.caseRehearsalSource ? `::case:${item.caseRehearsalSource.caseRecordId}` : ''}`;
}

export const QUOTE_TABLE_VIRTUALIZATION_THRESHOLD = 40;

export type ExpandableQuoteTableRow = {
  itemId: string;
  children?: readonly ExpandableQuoteTableRow[];
};

export function flattenVisibleQuoteTableRows<
  Row extends ExpandableQuoteTableRow,
>(rows: readonly Row[], expandedRowKeys: ReadonlySet<string>): Row[] {
  const visible: Row[] = [];
  const visit = (items: readonly Row[]) => {
    for (const item of items) {
      visible.push(
        item.children?.length
          ? ({ ...item, children: undefined } as Row)
          : item,
      );
      if (item.children?.length && expandedRowKeys.has(item.itemId))
        visit(item.children as readonly Row[]);
    }
  };
  visit(rows);
  return visible;
}

/** 与表格树展开语义一致地计算实际可见行，供虚拟表启停判断使用。 */
export function visibleQuoteTableRowCount(
  rows: readonly ExpandableQuoteTableRow[],
  expandedRowKeys: ReadonlySet<string>,
): number {
  let count = 0;
  const visit = (items: readonly ExpandableQuoteTableRow[]) => {
    for (const item of items) {
      count += 1;
      if (item.children?.length && expandedRowKeys.has(item.itemId))
        visit(item.children);
    }
  };
  visit(rows);
  return count;
}

export function totalQuoteTableRowCount(
  rows: readonly ExpandableQuoteTableRow[],
): number {
  return visibleQuoteTableRowCount(
    rows,
    new Set(rows.flatMap((row) => (row.children?.length ? [row.itemId] : []))),
  );
}

export function shouldVirtualizeQuoteTable(
  rows: readonly ExpandableQuoteTableRow[],
): boolean {
  return totalQuoteTableRowCount(rows) >= QUOTE_TABLE_VIRTUALIZATION_THRESHOLD;
}

/** 原紫色来源汇总仅是展示层；设备仍为真实设备叶子，不创建套餐身份。 */
export function quoteTableGroups(items: QuoteItem[]) {
  const rows: { key: string; grouped: boolean; items: QuoteItem[] }[] = [];
  const groups = new Map<string, (typeof rows)[number]>();
  for (const item of items) {
    if (!item.quoteGroupId && !item.packageId && item.sourceType !== 'device') {
      rows.push({ key: item.itemId, grouped: false, items: [item] });
      continue;
    }
    const key = quoteTableGroupKey(item);
    const existing = groups.get(key);
    if (existing) existing.items.push(item);
    else {
      const row = { key, grouped: true, items: [item] };
      groups.set(key, row);
      rows.push(row);
    }
  }
  return rows;
}

/**
 * 返回本次变更涉及的展示分组。工作台默认收起多层源，但新加入或
 * 数量变化的分组应自动展开，让用户立即看到这次操作的结果。
 */
export function changedQuoteTableGroupKeys(
  before: QuoteItem[],
  after: QuoteItem[],
) {
  const signatures = (items: QuoteItem[]) =>
    new Map(
      quoteTableGroups(items)
        .filter((group) => group.grouped)
        .map((group) => [
          group.key,
          group.items
            .map((item) => `${item.itemId}:${item.quantity}`)
            .join('\u0001'),
        ]),
    );
  const previous = signatures(before);
  return [...signatures(after)]
    .filter(([key, signature]) => previous.get(key) !== signature)
    .map(([key]) => `SOURCE-${key}`);
}

export function defaultExpandedQuoteTableGroupKeys(items: QuoteItem[]) {
  return quoteTableGroups(items)
    .filter((group) => group.grouped)
    .map((group) => `SOURCE-${group.key}`);
}

/** 汇总与实际套餐合并单元格使用同一组完整叶子，不能按来源 packageId 丢掉新增设备。 */
export function quoteDisplayedPackageItems(
  rows: readonly { packageGroup?: { sourceItems?: readonly QuoteItem[] } }[],
): QuoteItem[] {
  const items = new Map<string, QuoteItem>();
  for (const row of rows)
    for (const item of row.packageGroup?.sourceItems ?? [])
      items.set(item.itemId, item);
  return [...items.values()];
}

/** 使用报价实例身份获取套餐成员；追加设备不会因为没有 catalog packageId 而被漏掉。 */
export function quotePackageGroupItems(
  quote: Quote,
  item: QuoteItem,
): QuoteItem[] {
  const isPackage =
    Boolean(item.packageId) ||
    quote.containerInstances?.some(
      (instance) =>
        instance.id === item.containerInstanceId && instance.kind === 'package',
    ) ||
    quote.quoteGroups?.some(
      (group) =>
        group.id === item.quoteGroupId && group.kind === 'quote-local-package',
    );
  if (!isPackage) return [];
  const key = quoteTableGroupKey(item);
  return quote.items.filter(
    (candidate) => quoteTableGroupKey(candidate) === key,
  );
}
